// Status and usage of the gateway over the last 24 hours: headline figures, capacity with the
// 70 % warning, charts, and data used per tracker (for SIM data plans).
import { Link } from './Links';
import { GatewayMetrics } from '../api';
import { ago } from '../format';
import { MiniChart } from './MiniChart';

const HOUR = 3600_000;
export function bytes(n: number): string {
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}
const num = (n: number) => Math.round(n).toLocaleString('en-IE');
const compact = (n: number) => (n >= 1000 ? `${+(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K` : String(Math.round(n * 10) / 10));
const duration = (s: number) => (s < 3600 ? `${Math.round(s / 60)} min` : s < 86400 ? `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min` : `${Math.floor(s / 86400)} d ${Math.floor((s % 86400) / 3600)} h`);

function Tile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="gtile">
      <span className="gtile-label">{label}</span>
      <span className="gtile-value">{value}</span>
      {note && <span className="gtile-note">{note}</span>}
    </div>
  );
}

export function GatewayStatus({ m }: { m: GatewayMetrics }) {
  const cap = m.capacity;
  const share = cap ? cap.connections / Math.max(1, cap.max) : 0;
  const tracked = m.trackers.length;
  // Scale the measured hours up to a day and a month, for the SIM data estimate.
  const perDay = m.coveredHours > 0 ? 24 / Math.min(24, m.coveredHours) : 0;
  const mobileTotal = m.trackers.reduce((s, t) => s + t.mobileBytes, 0);
  const perTrackerMonth = tracked ? (mobileTotal / tracked) * perDay * 30 : 0;

  return (
    <>
      {m.alert.level !== 'ok' && (
        <p className={`gw-alert ${m.alert.level}`} role="alert">
          <strong>{m.alert.level === 'down' ? 'Gateway not answering.' : 'Capacity warning.'}</strong> {m.alert.message}
        </p>
      )}

      <div className="gtiles">
        <Tile label="Trackers connected" value={cap ? num(cap.trackers) : '–'} note={cap ? `${num(cap.connections)} connections · ${(share * 100).toFixed(share < 0.01 ? 2 : 0)} % of capacity` : 'gateway not answering'} />
        <Tile label="Data received" value={bytes(m.totals.bytesIn)} note={`sent ${bytes(m.totals.bytesOut)} · last ${m.coveredHours < 24 ? `${m.coveredHours} h (since measuring started)` : '24 h'}`} />
        <Tile label="Records" value={num(m.totals.records)} note={`${num(m.totals.packets)} packets · ${m.totals.packets ? bytes(m.totals.bytesIn / m.totals.packets) : '–'} each`} />
        <Tile label="Mobile data per tracker" value={tracked ? `${bytes(perTrackerMonth)}/month` : '–'} note="estimate incl. network overhead, at today's rate" />
        <Tile label="Packets now" value={cap ? `${cap.packetsPerSec.toFixed(cap.packetsPerSec < 10 ? 2 : 0)}/s` : '–'} note={cap ? `tested up to ${num(cap.packetsPerSecMax)}/s` : undefined} />
        <Tile label="Gateway process" value={cap ? `${Math.round(cap.process.rssMb)} MB` : '–'} note={cap ? `CPU ${cap.process.cpuPct.toFixed(1)} % · up ${duration(cap.process.uptimeS)}` : undefined} />
      </div>

      {cap && (
        <section className="card">
          <h2>Capacity <span className="muted small">· at most {num(cap.max)} trackers at once</span></h2>
          <div className="gmeter" role="meter" aria-valuemin={0} aria-valuemax={cap.max} aria-valuenow={cap.connections} aria-label="Connections in use">
            <span className={`gmeter-fill ${share >= cap.warnShare ? 'warn' : ''}`} style={{ width: `${Math.max(0.5, share * 100)}%` }} />
            <span className="gmeter-mark" style={{ left: `${cap.warnShare * 100}%` }} title="Warning from here" />
          </div>
          <p className="small muted">{num(cap.connections)} of {num(cap.max)} in use. A warning shows here and on the menu from {num(cap.warnAt)} ({Math.round(cap.warnShare * 100)} %). The smallest of these limits decides:</p>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Limit</th><th>Trackers</th><th>Based on</th></tr></thead>
              <tbody>
                {[...cap.limits].sort((a, b) => a.max - b.max).map((l) => (
                  <tr key={l.key} className={l.key === cap.binding ? 'gw-binding' : ''}>
                    <td>{l.label}{l.key === cap.binding && <span className="chip sub-expiring">decides</span>}</td>
                    <td>{num(l.max)}</td>
                    <td className="small muted">{l.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="small muted">Theoretical: measured with a load test on this server (16 Oct 2026), which also runs the database, Traccar and the apps. Real use stays far below it while trackers report every 10 s or less often.</p>
        </section>
      )}

      <section className="card">
        <h2>Last 24 hours</h2>
        {m.coveredHours < 24 && <p className="small muted">Measuring started {m.coveredHours} h ago, so the charts fill in over the day.</p>}
        <div className="gcharts">
          <MiniChart title="Data received per hour" kind="bar" stepMs={HOUR} format={bytes}
            points={m.hourly.map((h) => ({ t: h.t, v: h.bytesIn }))} />
          <MiniChart title="Records per hour" kind="bar" stepMs={HOUR} format={compact}
            points={m.hourly.map((h) => ({ t: h.t, v: h.records }))} />
          <MiniChart title="Trackers connected" kind="line" stepMs={10 * 60_000} format={compact}
            points={m.connections.map((c) => ({ t: c.t, v: c.trackers }))} />
        </div>
      </section>

      <section className="card">
        <h2>Data per tracker <span className="muted small">· last {m.coveredHours < 24 ? `${m.coveredHours} h` : '24 h'}</span></h2>
        {m.trackers.length === 0 ? <p className="muted">No data through the gateway yet.</p> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Unit</th><th>Records</th><th>Received</th><th>Connections</th><th>Mobile data / month</th><th>Last packet</th></tr></thead>
              <tbody>
                {m.trackers.map((t) => (
                  <tr key={t.unit?.trackerId}>
                    <td>{t.unit ? <Link to={t.unit.vehicle ? `/vehicles/${t.unit.vehicle.id}` : `/trackers/${t.unit.trackerId}`}>{t.unit.vehicle?.name ?? t.unit.imei}</Link> : '–'}
                      {t.model && <div className="small muted">{t.model}</div>}</td>
                    <td>{num(t.records)}</td>
                    <td>{bytes(t.bytes)}</td>
                    <td>{t.connections}</td>
                    <td>{bytes(t.mobileBytes * perDay * 30)}</td>
                    <td className="small">{ago(t.lastAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="small muted">Mobile data is an estimate: what the tracker sent, our answers, and about {m.overhead.perPacket} bytes of network overhead per packet ({m.overhead.perConnection} per new connection), scaled to 30 days. Mobile networks may round usage up per session.</p>
      </section>
    </>
  );
}
